import React, { useState, useEffect, useRef } from "react";
import QueryOutputSection from "../QueryOutputSection/QueryOutputSection";
import "./QueryRunSection.scss";
import {
  executeQuery,
  getDatabases,
  getTables,
  getColumns,
} from "../../../../../../../api/database_gui.js";
import { Controlled as CodeMirror } from "react-codemirror2";
import "codemirror/mode/sql/sql";
import "codemirror/addon/fold/foldcode";
import "codemirror/addon/fold/foldgutter";
import "codemirror/addon/fold/brace-fold";
import "codemirror/addon/fold/xml-fold";

import "codemirror/lib/codemirror.css";
import "codemirror/theme/material.css";
import "codemirror/theme/dracula.css";
import "codemirror/theme/eclipse.css";
import { toast } from "react-toastify";
import { Tooltip } from "@mui/material";
import { useResults } from "../../../ResultsProvider.js";
import { useAppTheme } from "../../../../../../../theme/useTheme";

import { FaPlay, FaCopy, FaTrash, FaEye, FaTimes, FaSync } from "react-icons/fa";
import { DiDatabase } from "react-icons/di";
import { LuMinimize2, LuMaximize2 } from "react-icons/lu";
import { IoIosArrowDown, IoIosArrowForward } from "react-icons/io";
import "../../../../../../common/css/common.scss";

function QueryRunSection({ resourceId, systemUserId, selectedDatabase, setSelectedDatabase, databaseInfo, resourceAccessID, refreshTrigger }) {
  const { isDark } = useAppTheme();
  const codeMirrorTheme = isDark ? "dracula" : "eclipse";
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("Query");
  const [queryHistory, setQueryHistory] = useState([]);
  const [queryStats, setQueryStats] = useState({
    totalRows: 0,
    rowsSelected: 0,
    queryTime: "00:00:00.000",
    cursorPos: "Ln 1, Col 1",
  });
  const [lines, setLines] = useState(["1"]);
  const [lineContent, setLineContent] = useState({});
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [databases, setDatabases] = useState([]);
  const [intendedDatabase, setIntendedDatabase] = useState("");
  const [message, setMessage] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [tables, setTables] = useState([]);
  const [tableColumns, setTableColumns] = useState({});
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [selectionStartLine, setSelectionStartLine] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const editorRef = useRef(null);
  const lineNumberRef = useRef(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshingDatabases, setRefreshingDatabases] = useState(false);
  const { viewresults } = useResults();
  const [outputHeight, setOutputHeight] = useState(60);
  const [columnInfo, setColumnInfo] = useState([]);

  // Enhanced query history state
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [expandedDateGroups, setExpandedDateGroups] = useState({});

  // Auto-expand date groups when they have queries
  useEffect(() => {
    const newExpandedGroups = {};
    Object.keys(groupedQueries).forEach(dateGroup => {
      newExpandedGroups[dateGroup] = true; // Auto-expand by default
    });
    setExpandedDateGroups(newExpandedGroups);
  }, [queryHistory]);

  // SQL Keywords by database type
  const sqlKeywords = {
    MySQL: [
      'SELECT', 'FROM', 'WHERE', 'INSERT', 'UPDATE', 'DELETE', 'CREATE', 'DROP', 'ALTER', 'TABLES', 'TABLE', 'DATABASES', 'DATABASE', 'INDEX', 'VIEW', 'TRIGGER', 'PROCEDURE', 'FUNCTION',
      'JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'INNER JOIN', 'OUTER JOIN', 'CROSS JOIN', 'UNION', 'UNION ALL', 'INTERSECT', 'EXCEPT',
      'GROUP BY', 'ORDER BY', 'HAVING', 'LIMIT', 'OFFSET', 'DISTINCT', 'COUNT', 'SUM', 'AVG', 'MAX', 'MIN', 'AS', 'ON', 'AND', 'OR', 'NOT', 'IN', 'EXISTS', 'BETWEEN', 'LIKE', 'IS NULL', 'IS NOT NULL',
      'INT', 'VARCHAR', 'TEXT', 'DATETIME', 'DATE', 'TIME', 'TIMESTAMP', 'DECIMAL', 'FLOAT', 'DOUBLE', 'BOOLEAN', 'BLOB', 'JSON',
      'PRIMARY KEY', 'FOREIGN KEY', 'UNIQUE', 'NOT NULL', 'DEFAULT', 'AUTO_INCREMENT', 'ENGINE', 'CHARSET', 'COLLATE',
      'SHOW', 'DESCRIBE', 'EXPLAIN', 'USE', 'GRANT', 'REVOKE', 'COMMIT', 'ROLLBACK', 'START TRANSACTION', 'SAVEPOINT',
      'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'IF', 'IFNULL', 'NULLIF', 'COALESCE', 'CONCAT', 'SUBSTRING', 'LENGTH', 'UPPER', 'LOWER'
    ],
    PostgreSQL: [
      'SELECT', 'FROM', 'WHERE', 'INSERT', 'UPDATE', 'DELETE', 'CREATE', 'DROP', 'ALTER', 'TABLES', 'TABLE', 'DATABASES', 'DATABASE', 'INDEX', 'VIEW', 'TRIGGER', 'PROCEDURE', 'FUNCTION',
      'JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'INNER JOIN', 'OUTER JOIN', 'CROSS JOIN', 'UNION', 'UNION ALL', 'INTERSECT', 'EXCEPT',
      'GROUP BY', 'ORDER BY', 'HAVING', 'LIMIT', 'OFFSET', 'DISTINCT', 'COUNT', 'SUM', 'AVG', 'MAX', 'MIN', 'AS', 'ON', 'AND', 'OR', 'NOT', 'IN', 'EXISTS', 'BETWEEN', 'LIKE', 'IS NULL', 'IS NOT NULL',
      'INTEGER', 'BIGINT', 'SMALLINT', 'VARCHAR', 'TEXT', 'TIMESTAMP', 'DATE', 'TIME', 'NUMERIC', 'REAL', 'DOUBLE PRECISION', 'BOOLEAN', 'BYTEA', 'JSON', 'JSONB',
      'PRIMARY KEY', 'FOREIGN KEY', 'UNIQUE', 'NOT NULL', 'DEFAULT', 'SERIAL', 'BIGSERIAL', 'SMALLSERIAL', 'CHECK', 'REFERENCES',
      'SHOW', 'DESCRIBE', 'EXPLAIN', 'GRANT', 'REVOKE', 'COMMIT', 'ROLLBACK', 'BEGIN', 'SAVEPOINT', 'RELEASE',
      'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'COALESCE', 'NULLIF', 'GREATEST', 'LEAST', 'CONCAT', 'SUBSTRING', 'LENGTH', 'UPPER', 'LOWER',
      'WITH', 'RECURSIVE', 'WINDOW', 'OVER', 'PARTITION BY', 'ROW_NUMBER', 'RANK', 'DENSE_RANK', 'LAG', 'LEAD'
    ],
    MSSQL: [
      'SELECT', 'FROM', 'WHERE', 'INSERT', 'UPDATE', 'DELETE', 'CREATE', 'DROP', 'ALTER', 'TABLES', 'TABLE', 'DATABASES', 'DATABASE', 'INDEX', 'VIEW', 'TRIGGER', 'PROCEDURE', 'FUNCTION',
      'JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'INNER JOIN', 'OUTER JOIN', 'CROSS JOIN', 'UNION', 'UNION ALL', 'INTERSECT', 'EXCEPT',
      'GROUP BY', 'ORDER BY', 'HAVING', 'TOP', 'OFFSET', 'FETCH', 'DISTINCT', 'COUNT', 'SUM', 'AVG', 'MAX', 'MIN', 'AS', 'ON', 'AND', 'OR', 'NOT', 'IN', 'EXISTS', 'BETWEEN', 'LIKE', 'IS NULL', 'IS NOT NULL',
      'INT', 'BIGINT', 'SMALLINT', 'TINYINT', 'VARCHAR', 'NVARCHAR', 'TEXT', 'NTEXT', 'DATETIME', 'DATETIME2', 'DATE', 'TIME', 'DECIMAL', 'FLOAT', 'REAL', 'BIT', 'VARBINARY', 'XML',
      'PRIMARY KEY', 'FOREIGN KEY', 'UNIQUE', 'NOT NULL', 'DEFAULT', 'IDENTITY', 'CHECK', 'REFERENCES', 'CLUSTERED', 'NONCLUSTERED',
      'SHOW', 'DESCRIBE', 'EXPLAIN', 'GRANT', 'REVOKE', 'COMMIT', 'ROLLBACK', 'BEGIN', 'SAVEPOINT', 'RELEASE',
      'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'ISNULL', 'NULLIF', 'COALESCE', 'CONCAT', 'SUBSTRING', 'LEN', 'UPPER', 'LOWER',
      'WITH', 'CTE', 'ROW_NUMBER', 'RANK', 'DENSE_RANK', 'LAG', 'LEAD', 'OVER', 'PARTITION BY', 'PIVOT', 'UNPIVOT'
    ]
  };

  // Enhanced query history structure - now uses actual stored data
  const enhancedQueryHistory = queryHistory.map((historyItem, index) => {
    // Handle both old format (string) and new format (object)
    const queryData = typeof historyItem === 'string' ? {
      query: historyItem,
      timestamp: new Date().toLocaleTimeString(),
      date: new Date().toLocaleDateString(),
      timestampMs: new Date().getTime(),
      rowsAffected: 0,
      duration: "0 msec",
      status: "Success"
    } : historyItem;

    // Use timestampMs if available, otherwise create date from date and time strings
    const executionDate = queryData.timestampMs ? new Date(queryData.timestampMs) : new Date(`${queryData.date} ${queryData.timestamp}`);
    const now = new Date();
    const isToday = executionDate.toDateString() === now.toDateString();
    const isYesterday = new Date(executionDate.getTime() + 24 * 60 * 60 * 1000).toDateString() === now.toDateString();
    
    let dateGroup;
    if (isToday) {
      dateGroup = "Today";
    } else if (isYesterday) {
      dateGroup = "Yesterday";
    } else {
      dateGroup = executionDate.toLocaleDateString();
    }

    return {
      id: index,
      query: queryData.query,
      timestamp: queryData.timestamp,
      date: queryData.date,
      dateGroup: dateGroup,
      rowsAffected: queryData.rowsAffected,
      duration: queryData.duration,
      status: queryData.status,
      isInternal: false
    };
  });

  // Group queries by date
  const groupedQueries = enhancedQueryHistory.reduce((groups, query) => {
    if (!groups[query.dateGroup]) {
      groups[query.dateGroup] = [];
    }
    groups[query.dateGroup].push(query);
    return groups;
  }, {});

  const fetchDatabasesRef = useRef(false); // Track if fetch is in progress or recently completed
  
  const fetchDatabases = async () => {
    // Prevent duplicate calls within 1 second
    if (fetchDatabasesRef.current) {
      return;
    }
    
    fetchDatabasesRef.current = true;
    try {
      const { data, error } = await getDatabases(resourceId, systemUserId);

      if (error) {
        setDatabases([]);
        return;
      }

      if (data && Array.isArray(data.databases)) {
        // Handle both string and array formats from backend
        const processedDatabases = data.databases.map(db => 
          Array.isArray(db) ? db[0] : db
        ).filter(db => db && typeof db === 'string');
        
        setDatabases(processedDatabases);
        
        if (processedDatabases.length > 0 && !selectedDatabase) {
          setSelectedDatabase(processedDatabases[0]);
        }
        
        // Database list updated successfully
      } else {
        // No databases found or invalid data format
        setDatabases([]);
      }
    } catch (error) {
      setDatabases([]);
    } finally {
      // Reset the flag after a short delay to allow legitimate refreshes
      setTimeout(() => {
        fetchDatabasesRef.current = false;
      }, 1000);
    }
  };

  // Fetch databases only on mount (not when selectedDatabase changes)
  useEffect(() => {
    fetchDatabases();
  }, []); // Empty dependency array - only run on mount

  // Refresh when selectedDatabase changes to a new value that's not in our list (for newly created databases)
  useEffect(() => {
    // Only refresh if selectedDatabase is set and it's not in our current databases list
    // Use a ref to avoid dependency on databases array which could cause loops
    if (selectedDatabase && databases.length > 0 && !databases.includes(selectedDatabase)) {
      fetchDatabases();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDatabase]); // Intentionally not including databases to avoid refresh loops

  // Listen for refresh trigger from parent component
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchDatabases();
    }
  }, [refreshTrigger]);

  // Set up periodic refresh to keep database list updated (less frequent for better performance)
  useEffect(() => {
    const interval = setInterval(() => {
      // Only refresh if we have databases or if it's been a while since last refresh
      if (databases.length === 0) {
        fetchDatabases();
      }
    }, 10000); // Refresh every 10 seconds, but only if no databases are loaded

    return () => clearInterval(interval);
  }, [databases.length]);

  const handleQueryChange = async (editor, data, value) => {
    setQuery(value);

    const shouldShowSuggestions = !value.trim().endsWith(";");
    const cursorPosition = editor.getCursor();
    const lineText = editor.getLine(cursorPosition.line);
    const lastWord = lineText.split(/\s+/).pop();

    if (lastWord) {
      let allSuggestions = [];
      
      // Get all words already typed in the current line to avoid suggesting duplicates
      const currentLineWords = lineText.split(/\s+/).filter(word => word.trim().length > 0);
      
      // Get all words from the entire query to avoid suggesting duplicates across the whole query
      const allQueryWords = value.split(/\s+/).filter(word => word.trim().length > 0);
      
      // Get SQL keywords based on database type
      if (databaseInfo && sqlKeywords[databaseInfo]) {
        const keywordSuggestions = sqlKeywords[databaseInfo].filter((keyword) =>
          keyword.toLowerCase().includes(lastWord.toLowerCase()) &&
          !allQueryWords.some(word => word.toLowerCase() === keyword.toLowerCase())
        );
        allSuggestions.push(...keywordSuggestions);
      }

      // Get database suggestions
      if (databases && databases.length > 0) {
        const databaseSuggestions = databases.filter((database) => {
          // Handle both string and array formats from backend
          const dbName = Array.isArray(database) ? database[0] : database;
          return dbName && typeof dbName === 'string' && 
                 dbName.toLowerCase().includes(lastWord.toLowerCase()) &&
                 !allQueryWords.some(word => word.toLowerCase() === dbName.toLowerCase());
        }).map(database => Array.isArray(database) ? database[0] : database);
        allSuggestions.push(...databaseSuggestions);
      }

      // Get table suggestions if database is selected
      if (selectedDatabase) {
        try {
          const { data: tablesData, error: tablesError } = await getTables(resourceId, systemUserId, selectedDatabase);
          if (!tablesError && tablesData && tablesData.tables) {
            // Handle the array of arrays format from backend
            const tableNames = tablesData.tables.map(table => Array.isArray(table) ? table[0] : table);
            const filteredTables = tableNames.filter((table) =>
              table && typeof table === 'string' && 
              table.toLowerCase().includes(lastWord.toLowerCase()) &&
              !allQueryWords.some(word => word.toLowerCase() === table.toLowerCase())
            );
            allSuggestions.push(...filteredTables);
          }
        } catch (error) {
          // Error handled silently
        }
      }

      // Get column suggestions based on context
      const tableNameFromContext = getTableNameFromContext(value, cursorPosition.line);
      if (tableNameFromContext && selectedDatabase) {
        try {
          // Check if we already have columns for this table
          if (!tableColumns[tableNameFromContext]) {
            const { data: columnsData, error: columnsError } = await getColumns(
              resourceId,
              systemUserId,
              selectedDatabase,
              tableNameFromContext
            );
            if (!columnsError && columnsData && columnsData.columns) {
              // Handle the array of arrays format from backend
              const columnNames = columnsData.columns.map(col => Array.isArray(col) ? col[0] : col);
              setTableColumns(prev => ({
                ...prev,
                [tableNameFromContext]: columnNames
              }));
            }
          }
          
          // Get column suggestions from cached data
          const cachedColumns = tableColumns[tableNameFromContext];
          if (cachedColumns && Array.isArray(cachedColumns)) {
            const columnSuggestions = cachedColumns.filter((column) =>
              column && typeof column === 'string' && 
              column.toLowerCase().includes(lastWord.toLowerCase()) &&
              !allQueryWords.some(word => word.toLowerCase() === column.toLowerCase())
            );
            allSuggestions.push(...columnSuggestions);
          }
        } catch (error) {
          // Error handled silently
        }
      }

      // Remove duplicates and sort
      const uniqueSuggestions = [...new Set(allSuggestions)];
      setSuggestions(uniqueSuggestions);
      setShowSuggestions(
        shouldShowSuggestions && uniqueSuggestions.length > 0
      );
      // Reset selected index when suggestions change
      setSelectedSuggestionIndex(-1);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }

    updateDropdownPosition(editor);
  };

  const updateDropdownPosition = (editor) => {
    const cursorPosition = editor.getCursor();
    const coords = editor.charCoords(cursorPosition, "local");
    setDropdownPosition({ top: coords.top, left: coords.left });
  };

  // Helper function to detect the table name from the current query context
  const getTableNameFromContext = (queryText, cursorLine) => {
    if (!queryText || cursorLine < 0) return null;
    
    const lines = queryText.split('\n');
    const currentLine = lines[cursorLine] || '';
    
    // Look for FROM clause in the current line or previous lines
    const fromMatch = currentLine.match(/FROM\s+(\w+)/i);
    if (fromMatch) {
      return fromMatch[1];
    }
    
    // Look for JOIN clauses
    const joinMatch = currentLine.match(/(?:JOIN|LEFT\s+JOIN|RIGHT\s+JOIN|INNER\s+JOIN|OUTER\s+JOIN)\s+(\w+)/i);
    if (joinMatch) {
      return joinMatch[1];
    }
    
    // Look for table references in WHERE, ORDER BY, GROUP BY clauses
    const tableRefMatch = currentLine.match(/(?:WHERE|ORDER\s+BY|GROUP\s+BY|HAVING)\s+(\w+)\./i);
    if (tableRefMatch) {
      return tableRefMatch[1];
    }
    
    // If not found in current line, search previous lines for FROM clause
    for (let i = cursorLine - 1; i >= 0; i--) {
      const line = lines[i];
      const fromMatch = line.match(/FROM\s+(\w+)/i);
      if (fromMatch) {
        return fromMatch[1];
      }
      const joinMatch = line.match(/(?:JOIN|LEFT\s+JOIN|RIGHT\s+JOIN|INNER\s+JOIN|OUTER\s+JOIN)\s+(\w+)/i);
      if (joinMatch) {
        return joinMatch[1];
      }
    }
    
    return null;
  };

  const handleDatabaseChange = async (event) => {
    const database = event.target.value;
    setSelectedDatabase(database);
    setIntendedDatabase("");
    setQuery("");
    setTableColumns({}); // Clear cached columns when database changes

    try {
      const { data: tablesData, error: tablesError } = await getTables(resourceId, systemUserId, database);
      if (!tablesError && tablesData && tablesData.tables) {
        // Handle the array of arrays format from backend
        const tableNames = tablesData.tables.map(table => Array.isArray(table) ? table[0] : table);
        setTables(tableNames);
        toast.success("Database changed successfully!");
      } else {
        setTables([]);
        toast.error("Error fetching tables.");
      }
    } catch (error) {
      setTables([]);
      toast.error("Error changing database.");
    }
  };

  const handleDropdownClick = async () => {
    // Existing implementation
  };

  const handleRefreshDatabases = async () => {
    try {
      setRefreshingDatabases(true);
      await fetchDatabases();
      toast.success("Database list refreshed successfully!");
    } catch (error) {
      toast.error("Failed to refresh database list");
    } finally {
      setRefreshingDatabases(false);
    }
  };

  const handleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  const getSelectedText = () => {
    const editor = editorRef.current?.editor;
    if (!editor) return "";
    return editor.getSelection();
  };

  const getQueryFromCursor = (cursorPosition) => {
    const editor = editorRef.current?.editor;
    if (!editor) return "";

    const selectedText = editor.getSelection();
    if (selectedText) {
      return selectedText;
    }

    const lineText = editor.getLine(cursorPosition.line);
    return lineText.trim();
  };

  // Helper function to filter out commented lines from query
  const filterCommentedLines = (queryText) => {
    if (!queryText) return "";
    
    // Ensure queryText is a string
    const text = String(queryText);
    
    // First, remove block comments /* ... */
    let processedText = text.replace(/\/\*[\s\S]*?\*\//g, '');
    
    const lines = processedText.split('\n');
    const filteredLines = lines.filter(line => {
      const trimmedLine = line.trim();
      // Remove lines that start with -- (SQL comments) or are empty
      // Also handle cases where there might be whitespace before --
      return !trimmedLine.startsWith('--') && trimmedLine.length > 0;
    });
    
    // Clean up any extra whitespace and ensure proper spacing
    const result = filteredLines.join('\n').trim();
    
    // Remove any empty lines that might be left after filtering
    return result.replace(/\n\s*\n/g, '\n').trim();
  };

  const handleExecuteQuery = async (customQuery = null) => {
    const startTime = performance.now();
    const selectedText = getSelectedText();
    
    // More defensive approach to ensure we always have a string
    let queryToExecute;
    try {
      queryToExecute = String(customQuery || selectedText || query || "");
    } catch (error) {
      queryToExecute = "";
    }

    if (!queryToExecute || !queryToExecute.trim) {
      toast.error("Invalid query data. Please try again.");
      return;
    }

    if (!queryToExecute.trim()) {
      toast.error("Please enter a query to execute.");
      return;
    }

    // Filter out commented lines from the query
    const filteredQuery = filterCommentedLines(queryToExecute);

    if (!filteredQuery || typeof filteredQuery !== 'string') {
      toast.error("No executable query found. All lines are commented out.");
      return;
    }

    try {
      // Ensure we have a valid database selected
      if (!selectedDatabase) {
        toast.error("Please select a database first.");
        return;
      }
      
      const databaseName = Array.isArray(selectedDatabase)
        ? selectedDatabase[0]
        : selectedDatabase;
      setLoading(true);

      const { message, data, error, columns } = await executeQuery(
        resourceId,
        systemUserId,
        filteredQuery,
        databaseName,
        resourceAccessID
      );

      setLoading(false);

      const endTime = performance.now();
      const queryTime = ((endTime - startTime) / 1000).toFixed(3);



      // Check for various error conditions including SQL errors in message
      const hasError = error || !data || (message && (
        message.toLowerCase().includes('error') || 
        message.toLowerCase().includes('failed') ||
        message.toLowerCase().includes('invalid') ||
        message.toLowerCase().includes('syntax')
      ));
      
      if (hasError) {
        const errorMessage = error || message || "Query execution failed";
        setError(errorMessage);
        setResults([]);
        setColumnInfo([]);
        
        // Store failed query in history
        const executionTime = new Date();
        const queryHistoryItem = {
          query: filteredQuery,
          timestamp: executionTime.toLocaleTimeString(),
          date: executionTime.toLocaleDateString(),
          timestampMs: executionTime.getTime(), // Store actual timestamp for date grouping
          rowsAffected: 0,
          duration: queryTime < 1 ? `${(queryTime * 1000).toFixed(0)} msec` : `${queryTime} sec`,
          status: "Error"
        };
        setQueryHistory((prevHistory) => [...prevHistory, queryHistoryItem]);
        
        setQueryStats({
          totalRows: 0,
          rowsSelected: 0,
          queryTime: "00:00:00.000",
        });
        setMessage(null);
        toast.error(`Error: ${errorMessage}`);
      } else {
        // Only mark as success if we have valid data and no error indicators
        const isSuccess = data && Array.isArray(data) && !message?.toLowerCase().includes('error');
        
        setResults(Array.isArray(data) ? data : []);
        // Store column information for displaying types
        if (columns && Array.isArray(columns)) {
          setColumnInfo(columns);
        } else {
          setColumnInfo([]);
        }
        
        // Store query with actual execution data
        const executionTime = new Date();
        const queryHistoryItem = {
          query: filteredQuery,
          timestamp: executionTime.toLocaleTimeString(),
          date: executionTime.toLocaleDateString(),
          timestampMs: executionTime.getTime(), // Store actual timestamp for date grouping
          rowsAffected: Array.isArray(data) ? data.length : 0,
          duration: queryTime < 1 ? `${(queryTime * 1000).toFixed(0)} msec` : `${queryTime} sec`,
          status: isSuccess ? "Success" : "Error"
        };
        setQueryHistory((prevHistory) => [...prevHistory, queryHistoryItem]);
        setQueryStats({
          totalRows: Array.isArray(data) ? data.length : 0,
          rowsSelected: Array.isArray(data) ? data.length : 0,
          queryTime: `00:00:${queryTime}`,
        });
        setError(null);
        setMessage(message);
        
        if (isSuccess) {
          toast.success(message);
        } else {
          toast.error(message || "Query execution failed");
        }
      }
    } catch (e) {
      const endTime = performance.now();
      const queryTime = ((endTime - startTime) / 1000).toFixed(3);
      
              // Store failed query in history for catch block errors
        const executionTime = new Date();
        const queryHistoryItem = {
          query: filteredQuery,
          timestamp: executionTime.toLocaleTimeString(),
          date: executionTime.toLocaleDateString(),
          timestampMs: executionTime.getTime(), // Store actual timestamp for date grouping
          rowsAffected: 0,
          duration: queryTime < 1 ? `${(queryTime * 1000).toFixed(0)} msec` : `${queryTime} sec`,
          status: "Error"
        };
      setQueryHistory((prevHistory) => [...prevHistory, queryHistoryItem]);
      
      setError(e.message || "An error occurred while executing the query");
      setLoading(false);
      setResults([]);
      setQueryStats({
        totalRows: 0,
        rowsSelected: 0,
        queryTime: "00:00:00.000",
      });
      setMessage(null);
      toast.error(`Error: ${e.message || "An unknown error occurred"}`);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "Query") {
      const editor = editorRef.current?.editor;
      const editorValue = editor ? editor.getValue() : "";
      setLines(editorValue.split("\n").map((_, i) => String(i + 1)));
    }
  };

  const handleHistoryClick = (historyQuery) => {
    // Handle both old format (string) and new format (object)
    const queryText = typeof historyQuery === 'string' ? historyQuery : historyQuery.query;
    setQuery(queryText);
    setActiveTab("Query");
    setShowSuggestions(false);
  };

  const handleHistoryItemSelect = (item) => {
    setSelectedHistoryItem(item);
  };

  const handleHistoryItemExecute = (item) => {
    setQuery(item.query);
    setActiveTab("Query");
    setShowSuggestions(false);
    
    // Execute the query directly with the history item's query
    handleExecuteQuery(item.query);
  };

  const handleHistoryItemCopy = (item) => {
    navigator.clipboard.writeText(item.query);
    toast.success("Query copied to clipboard!");
  };

  const handleHistoryItemCopyToEditor = (item) => {
    setQuery(item.query);
    setActiveTab("Query");
    setShowSuggestions(false);
    toast.success("Query copied to editor!");
  };

  const handleRemoveHistoryItem = (itemId) => {
    setQueryHistory(prev => prev.filter((_, index) => index !== itemId));
    if (selectedHistoryItem && selectedHistoryItem.id === itemId) {
      setSelectedHistoryItem(null);
    }
    toast.success("Query removed from history!");
  };

  const handleRemoveAllHistory = () => {
    setQueryHistory([]);
    setSelectedHistoryItem(null);
    toast.success("All queries removed from history!");
  };

  const toggleDateGroup = (dateGroup) => {
    setExpandedDateGroups(prev => ({
      ...prev,
      [dateGroup]: !prev[dateGroup]
    }));
  };

  const handleSuggestionClick = (suggestion) => {
    const editor = editorRef.current?.editor;
    if (!editor) return;

    const cursor = editor.getCursor();
    const lineText = editor.getLine(cursor.line);

    const lastWordMatch = lineText.match(/\b\w+$/);
    const lastWord = lastWordMatch ? lastWordMatch[0] : "";

    const newLineText =
      lineText.slice(0, lineText.length - lastWord.length) + suggestion;

    editor.replaceRange(
      newLineText,
      { line: cursor.line, ch: 0 },
      { line: cursor.line, ch: lineText.length }
    );

    editor.setCursor(
      cursor.line,
      cursor.ch - lastWord.length + suggestion.length
    );
    editor.focus();

    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
  };

  useEffect(() => {
    if (viewresults?.query) {
      setQuery(viewresults.query + ";");
      setResults(Array.isArray(viewresults.data) ? viewresults.data : []);
      
      // Add sidebar query to query history (only if it's not already there)
      const queryHistoryItem = {
        id: Date.now() + Math.random(),
        query: viewresults.query,
        timestamp: new Date().toLocaleTimeString(),
        date: new Date().toLocaleDateString(),
        status: 'Success',
        duration: '0.000',
        rowsAffected: Array.isArray(viewresults.data) ? viewresults.data.length : 0,
        source: 'sidebar'
      };
      
      setQueryHistory((prevHistory) => {
        // Check if this query is already in the history to avoid duplicates
        const isDuplicate = prevHistory.some(item => 
          item.query === viewresults.query && 
          item.source === 'sidebar' &&
          Math.abs(new Date(item.timestamp) - new Date()) < 1000 // Within 1 second
        );
        
        if (isDuplicate) {
          return prevHistory;
        }
        
        return [...prevHistory, queryHistoryItem];
      });
    }
  }, [viewresults.query, viewresults.data]);

  useEffect(() => {
    const scrollCursorAboveOutput = () => {
      if (editorRef.current) {
        const editor = editorRef.current.editor;
        const cursorPos = editor.getCursor();
        const lineHeight = editor.defaultTextHeight();
        const viewportHeight = editor.getScrollerElement().clientHeight;
        const scrollPosition =
          (cursorPos.line + 1000) * lineHeight - viewportHeight * 0.1;
        if (scrollPosition > 0) {
          editor.scrollTo(0, scrollPosition);
        }
      }
    };
    if (editorRef.current) {
      const editor = editorRef.current.editor;
      editor.on("cursorActivity", scrollCursorAboveOutput);
    }
  }, [editorRef]);

  // Enhanced Query History Component
  const renderQueryHistory = () => {
    return (
      <div className="database-gui-enhanced-query-history">
        {/* Remove All Button */}
        {queryHistory.length > 0 && (
          <div className="database-gui-remove-all-section">
            <button 
              className="database-gui-remove-all-button"
              onClick={handleRemoveAllHistory}
            >
              <FaTimes size={10} />
              Remove All
            </button>
          </div>
        )}
        
        {/* Query History Content */}
        <div className="database-gui-query-history-content">
          <div className="database-gui-query-list-panel w-30">
            {Object.keys(groupedQueries).length > 0 ? (
              Object.entries(groupedQueries).map(([dateGroup, queries]) => (
                <div key={dateGroup} className="database-gui-date-group">
                  <div 
                    className="database-gui-database-gui-date-group-header"
                    onClick={() => toggleDateGroup(dateGroup)}
                  >
                    {expandedDateGroups[dateGroup] ? (
                      <IoIosArrowDown className="database-gui-expand-icon" />
                    ) : (
                      <IoIosArrowForward className="database-gui-expand-icon" />
                    )}
                    <span className="database-gui-database-gui-date-group-title">
                      {dateGroup} - {queries[0].date}
                    </span>
                  </div>
                  {expandedDateGroups[dateGroup] && (
                    <div className="database-gui-database-gui-date-group-queries">
                      {queries.map((queryItem) => (
                        <div
                          key={queryItem.id}
                          className={`overflow-auto database-gui-query-item ${
                            selectedHistoryItem?.id === queryItem.id ? 'selected' : ''
                          }`}
                          onClick={() => handleHistoryItemSelect(queryItem)}
                        >
                          <div className="database-gui-database-gui-query-item-header">
                            <IoIosArrowForward className="database-gui-query-icon" />
                            <span className="database-gui-query-text">{queryItem.query}</span>
                            <span className={`database-gui-status-indicator ${queryItem.status === 'Success' ? 'success' : 'error'}`}>
                              {queryItem.status === 'Success' ? '✓' : '✗'}
                            </span>
                          </div>
                          <div className="database-gui-database-gui-query-item-time">{queryItem.timestamp}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="database-gui-no-history">
                <p>No query history available.</p>
              </div>
            )}
          </div>

          {/* Query Details Panel */}
          <div className="database-gui-database-gui-query-details-panel w-70">
            {selectedHistoryItem ? (
              <div className="database-gui-query-details px-4">
                <div className="database-gui-database-gui-query-details-header">
                  <h3>Query Details</h3>
                </div>
                <div className="database-gui-database-gui-query-details-content">
                  <div className="database-gui-detail-row">
                    <span className="database-gui-detail-label">Date:</span>
                    <span className="database-gui-detail-value">{selectedHistoryItem.date}</span>
                  </div>
                  <div className="database-gui-detail-row">
                    <span className="database-gui-detail-label">Time:</span>
                    <span className="database-gui-detail-value">{selectedHistoryItem.timestamp}</span>
                  </div>
                  <div className="database-gui-detail-row">
                    <span className="database-gui-detail-label">Rows affected:</span>
                    <span className="database-gui-detail-value">{selectedHistoryItem.rowsAffected}</span>
                  </div>
                  <div className="database-gui-detail-row">
                    <span className="database-gui-detail-label">Duration:</span>
                    <span className="database-gui-detail-value">{selectedHistoryItem.duration}</span>
                  </div>
                  <div className="database-gui-detail-row">
                    <span className="database-gui-detail-label">Status:</span>
                    <span className={`database-gui-detail-value ${selectedHistoryItem.status === 'Success' ? 'database-gui-status-success' : 'database-gui-status-error'}`}>
                      {selectedHistoryItem.status}
                    </span>
                  </div>
                </div>
                <div className="database-gui-query-actions">
                  <button 
                    className="database-gui-action-button simple"
                    onClick={() => handleHistoryItemExecute(selectedHistoryItem)}
                  >
                    <FaPlay size={10} />
                    Execute
                  </button>
                  <button 
                    className="database-gui-action-button simple"
                    onClick={() => handleHistoryItemCopy(selectedHistoryItem)}
                  >
                    <FaCopy size={10} />
                    Copy
                  </button>
                  <button 
                    className="database-gui-action-button simple"
                    onClick={() => handleHistoryItemCopyToEditor(selectedHistoryItem)}
                  >
                    <FaEye size={10} />
                    Copy to Editor
                  </button>
                  <button 
                    className="database-gui-action-button simple"
                    onClick={() => handleRemoveHistoryItem(selectedHistoryItem.id)}
                  >
                    <FaTimes size={10} />
                    Remove
                  </button>
                </div>
                <div className="database-gui-database-gui-query-text-display">
                  <h4>Query:</h4>
                  <pre className="database-gui-query-code">{selectedHistoryItem.query}</pre>
                </div>
                <div className="database-gui-query-messages">
                  <h4>Messages:</h4>
                  <div className={`message ${selectedHistoryItem.status === 'Success' ? 'success' : 'error'}`}>
                    {selectedHistoryItem.status === 'Success' 
                      ? `Successfully run. Total query runtime: ${selectedHistoryItem.duration}. ${selectedHistoryItem.rowsAffected} rows affected.`
                      : `Query failed. Total query runtime: ${selectedHistoryItem.duration}.`
                    }
                  </div>
                </div>
              </div>
            ) : (
              <div className="database-gui-no-selection">
                <p>Select a query from the history to view details.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="database-gui-query-navbar-fullcontainer">
      <div className="database-gui-query-navbar-container">
        <div className="database-gui-query-navbar-section">
          <div className="database-gui-query-navbar-section-left">
            <div className="database-gui-query-navbar-section-left1">
              <button
                className={`database-gui-navbar-button-left ${
                  activeTab === "Query" ? "active" : ""
                }`}
                onClick={() => handleTabChange("Query")}
                style={{ height: "33px", marginLeft: "0" }}
              >
                Query
              </button>
            </div>
            <div className="database-gui-query-navbar-section-left2">
              <button
                className={`database-gui-navbar-button-left ${
                  activeTab === "Query History" ? "active" : ""
                }`}
                onClick={() => handleTabChange("Query History")}
              >
                Query History
              </button>
            </div>
          </div>
          <div className="database-gui-query-navbar-section-right">
            <div className="database-selector-wrapper">
              <DiDatabase
                size={20}
                className="database-icon"
              />
              <select
                value={selectedDatabase}
                onChange={handleDatabaseChange}
                onClick={handleDropdownClick}
                className="database-dropdown"
              >
                {databases.length > 0 ? (
                  databases.map((db, index) => (
                    <option key={index} value={db}>
                      {db}
                    </option>
                  ))
                ) : (
                  <option disabled>No databases available - Click refresh button</option>
                )}
              </select>
              <Tooltip title="Refresh Database List">
                <button
                  className={`refresh-button ${refreshingDatabases ? "refreshing" : ""}`}
                  onClick={handleRefreshDatabases}
                  disabled={refreshingDatabases}
                >
                  <FaSync 
                    size={12} 
                    className="refresh-icon"
                  />
                </button>
              </Tooltip>
            </div>
            <div className="database-gui-tooltip-container">
              <Tooltip title="Execute Query (ctrl + Enter)">
                <button
                  className="database-gui-navbar-button-right"
                  onClick={() => handleExecuteQuery()}
                  disabled={!query.trim()}
                >
                  <FaPlay
                    size={18}
                    className="play-icon"
                  />
                </button>
              </Tooltip>
            </div>
            <button
              className="database-gui-maximizeminimizebutton-icon"
              onClick={handleMaximize}
            >
              {isMaximized ? (
                <LuMinimize2 size={20} />
              ) : (
                <LuMaximize2 size={20} />
              )}
            </button>
          </div>
        </div>
      </div>
      <div>
        <div className="database-gui-query-run-section-container">
          <div className="database-gui-query-run-section">
            <div className="database-gui-query-run-section-right">
              {activeTab === "Query" ? (
                <div
                  className={`database-gui-query-run-section-editor-container ${
                    isMaximized ? "fullscreen" : ""
                  }`}
                  style={{
                    height: `${isMaximized ? 200 : 110 - outputHeight}%`,
                    overflow: "auto",
                    margin: 0,
                    padding: 0,
                  }}
                >
                  <CodeMirror
                    ref={editorRef}
                    value={query}
                    options={{
                      mode: "sql",
                      lineNumbers: true,
                      theme: codeMirrorTheme,
                      extraKeys: {
                        "Ctrl-Enter": () => handleExecuteQuery(),
                        "Tab": (editor) => {
                          if (showSuggestions && suggestions.length > 0) {
                            // Auto-complete with selected suggestion or first suggestion
                            const selectedSuggestion = selectedSuggestionIndex >= 0 
                              ? suggestions[selectedSuggestionIndex] 
                              : suggestions[0];
                            handleSuggestionClick(selectedSuggestion);
                          } else {
                            // Default tab behavior
                            editor.replaceSelection("  ");
                          }
                        },
                        "ArrowDown": (editor) => {
                          if (showSuggestions && suggestions.length > 0) {
                            setSelectedSuggestionIndex(prev => 
                              prev < suggestions.length - 1 ? prev + 1 : prev
                            );
                            return false;
                          }
                        },
                        "ArrowUp": (editor) => {
                          if (showSuggestions && suggestions.length > 0) {
                            setSelectedSuggestionIndex(prev => 
                              prev > 0 ? prev - 1 : -1
                            );
                            return false;
                          }
                        },
                        "Escape": (editor) => {
                          setShowSuggestions(false);
                          setSelectedSuggestionIndex(-1);
                        },
                        "Ctrl-/": (editor) => {
                          const selection = editor.getSelection();

                          if (selection) {
                            const selectedLines = selection.split("\n");
                            const startLine = editor.getCursor(true).line;
                            const endLine = editor.getCursor(false).line;
                            const allCommented = selectedLines.every((line) =>
                              line.trim().startsWith("--")
                            );

                            if (allCommented) {
                              const uncommentedLines = selectedLines.map(
                                (line) => line.replace(/^--\s*/, "")
                              );
                              editor.replaceRange(
                                uncommentedLines.join("\n"),
                                { line: startLine, ch: 0 },
                                { line: endLine + 1, ch: 0 }
                              );
                            } else {
                              const commentedLines = selectedLines.map(
                                (line) => `-- ${line}`
                              );
                              editor.replaceRange(
                                commentedLines.join("\n"),
                                { line: startLine, ch: 0 },
                                { line: endLine + 1, ch: 0 }
                              );
                            }

                            editor.setCursor({ line: endLine, ch: 0 });
                          } else {
                            const cursorLine = editor.getCursor().line;
                            const lineText = editor.getLine(cursorLine);
                            if (lineText.trim().startsWith("--")) {
                              editor.replaceRange(
                                lineText.replace(/^--\s*/, ""),
                                { line: cursorLine, ch: 0 },
                                { line: cursorLine, ch: lineText.length }
                              );
                            } else {
                              editor.replaceRange(`--`, {
                                line: cursorLine,
                                ch: 0,
                              });
                            }
                            editor.setCursor(cursorLine, lineText.length + 3);
                          }
                        },
                      },
                    }}
                    onBeforeChange={handleQueryChange}
                    onChange={handleQueryChange}
                    style={{
                      position: "fixed",
                      overflowY: "scroll",
                      fontSize: "14px",
                      backgroundColor: "white",
                      lineHeight: "1.7",
                      height: "100%",
                      fontFamily:
                        "ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
                    }}
                  />

                  {showSuggestions && suggestions.length > 0 && (
                    <div
                      className="database-gui-suggestions-dropdown"
                      style={{
                        position: "absolute",
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        backgroundColor: "white",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        zIndex: 1000,
                        maxHeight: "200px",
                        overflowY: "auto",
                        minWidth: "200px"
                      }}
                    >
                      {suggestions.map((suggestion, index) => {
                        const isKeyword = databaseInfo && sqlKeywords[databaseInfo] && 
                          sqlKeywords[databaseInfo].includes(suggestion);
                        const isDatabase = databases && databases.some(db => {
                          const dbName = Array.isArray(db) ? db[0] : db;
                          return dbName === suggestion;
                        });
                        
                        // Check if it's a column by looking through all cached table columns
                        const isColumn = Object.values(tableColumns).some(columns => 
                          columns && Array.isArray(columns) && 
                          columns.some(col => col === suggestion)
                        );
                        
                        const isTable = !isKeyword && !isDatabase && !isColumn;
                        
                        // Determine label and styling
                        let label, labelBgColor, textColor, fontWeight;
                        if (isKeyword) {
                          label = "KEYW";
                          labelBgColor = "#ff6b35";
                          textColor = "#007bff";
                          fontWeight = "600";
                        } else if (isDatabase) {
                          label = "SCMA";
                          labelBgColor = "#6f42c1";
                          textColor = "#6f42c1";
                          fontWeight = "600";
                        } else if (isColumn) {
                          label = "COL";
                          labelBgColor = "#17a2b8";
                          textColor = "#17a2b8";
                          fontWeight = "600";
                        } else {
                          label = "TBL";
                          labelBgColor = "#28a745";
                          textColor = "#333";
                          fontWeight = "400";
                        }
                        
                        return (
                          <div
                            key={index}
                            className={`database-gui-suggestion-item ${
                              index === selectedSuggestionIndex ? "selected" : ""
                            }`}
                            onClick={() => handleSuggestionClick(suggestion)}
                            style={{ 
                              padding: "8px 12px", 
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              borderBottom: "1px solid #f0f0f0",
                              fontSize: "13px"
                            }}
                          >
                            <span style={{ 
                              backgroundColor: labelBgColor,
                              color: "white",
                              fontWeight: "600",
                              fontSize: "10px",
                              padding: "2px 6px",
                              borderRadius: "3px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              minWidth: "32px",
                              textAlign: "center"
                            }}>
                              {label}
                            </span>
                            <span style={{ 
                              color: textColor,
                              fontWeight: fontWeight
                            }}>
                              {suggestion}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                renderQueryHistory()
              )}
            </div>
          </div>
        </div>
        {!isFullScreen && (
          <div className="database-gui-query-output-section-query-run-section">
            <QueryOutputSection
              results={results}
              error={error}
              message={message}
              resourceId={resourceId}
              systemUserId={systemUserId}
              columnInfo={columnInfo}
              onHeightChange={(newHeight) => setOutputHeight(newHeight)}
              resourceAccessID={resourceAccessID}
              selectedDatabase={selectedDatabase}
              currentQuery={query}
              onRefreshResults={() => handleExecuteQuery(query)}
            />
          </div>
        )}
      </div>
      <div className="database-gui-query-run-section-bottom-navbar" style={{ zIndex: "1000" }}>
        <span
          className="database-gui-query-run-section-bottom-navbar-text"
          style={{ marginLeft: "10px" }}
        >
          {" "}
          Total rows: {queryStats.totalRows} of {queryStats.totalRows}
        </span>
        <span
          className="database-gui-query-run-section-bottom-navbar-text"
          style={{ marginLeft: "20px" }}
        >
          {" "}
          Query complete {queryStats.queryTime}
        </span>
      </div>
    </div>
  );
}

export default QueryRunSection;
